import { QdzElement, adoptSectionStyles, define, motion, parseHTML } from '@qdz/element';

const SHOW = [9, 12, 18, 24];
const VIEWS = ['list', '1', '2', '3', '4'];
const KEY = 'qdz:collection-view';
const MOBILE = window.matchMedia('(max-width: 749px)');
const TABLET = window.matchMedia('(min-width: 750px) and (max-width: 1199px)');
const PROPS = ['--qdz-columns', '--qdz-columns-tablet', '--qdz-columns-mobile'];
const fits = (view) => view === 'list' || (MOBILE.matches ? ['1', '2'] : ['2', '3', '4']).includes(view);

const fresh = (item) => {
  const copy = item.cloneNode(true);
  copy.removeAttribute('data-reveal');
  copy.removeAttribute('data-revealed');
  copy.style.removeProperty('--qdz-reveal-index');
  if (!copy.getAttribute('style')) copy.removeAttribute('style');
  for (const image of copy.querySelectorAll('img[fetchpriority="high"]')) image.removeAttribute('fetchpriority');
  for (const image of copy.querySelectorAll('img[loading="eager"]')) image.loading = 'lazy';
  return copy.outerHTML;
};

const stored = () => {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
};

export class QdzResults extends QdzElement {
  #cache = new Map();
  #loaded = 0;
  #observer = null;
  #busy = false;
  #base = {};

  get grid() {
    return this.ref('grid');
  }

  get nav() {
    return this.querySelector('.qdz-pagination');
  }

  get size() {
    return Number(this.getAttribute('page-size')) || 12;
  }

  get total() {
    return Number(this.getAttribute('total')) || 0;
  }

  get params() {
    return new URL(window.location.href).searchParams;
  }

  get show() {
    const value = Number(this.params.get('show'));
    return SHOW.includes(value) ? value : this.size;
  }

  get mode() {
    return this.params.get('paging') === 'numbers' ? 'numbers' : this.getAttribute('mode') || 'numbers';
  }

  get view() {
    const value = this.params.get('columns') || stored();
    return VIEWS.includes(value) ? value : null;
  }

  mount() {
    this.#cache.set(Number(this.getAttribute('page')) || 1, [...this.grid.children].map(fresh));
    this.#base = Object.fromEntries(PROPS.map((name) => [name, this.grid.style.getPropertyValue(name)]));
    this.applyView(this.view);
    this.listen(MOBILE, 'change', () => this.applyView(this.view));
    this.listen(TABLET, 'change', () => this.applyView(this.view));
    this.listen(this, 'click', (event) => this.#onClick(event));
    this.#sync();
    const page = Math.max(Number(this.params.get('page')) || 1, 1);
    if (this.mode === 'numbers') {
      if (this.show !== this.size) this.renderPage(page);
    } else {
      this.#loaded = 0;
      this.#start(page);
    }
  }

  unmount() {
    this.#observer?.disconnect();
  }

  applyView(view) {
    const chosen = view && fits(view) ? view : null;
    const list = chosen === 'list';
    this.grid.classList.toggle('qdz-product-grid--list', list);
    for (const name of PROPS) {
      const value = chosen ? (list ? '1' : chosen) : this.#base[name];
      if (value) this.grid.style.setProperty(name, value);
      else this.grid.style.removeProperty(name);
    }
    const desktop = this.getAttribute('columns');
    const shown = MOBILE.matches ? this.getAttribute('columns-mobile') : TABLET.matches ? String(Math.min(Number(this.getAttribute('columns-tablet')), Number(desktop))) : desktop;
    const active = chosen ?? shown;
    for (const button of document.querySelectorAll('[data-view]')) button.setAttribute('aria-pressed', String(button.dataset.view === active));
  }

  setView(view) {
    try {
      localStorage.setItem(KEY, view);
    } catch {}
    this.#replace({ columns: view });
    this.applyView(view);
  }

  setShow(show) {
    this.#replace({ show: show === this.size ? null : String(show), page: null });
    this.#sync();
    if (this.mode === 'numbers') this.renderPage(1);
    else {
      this.#loaded = 0;
      this.#start(1);
    }
  }

  #sync() {
    for (const button of document.querySelectorAll('[data-show]')) button.setAttribute('aria-pressed', String(Number(button.dataset.show) === this.show));
  }

  #replace(changes, push = false) {
    const url = new URL(window.location.href);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === undefined) url.searchParams.delete(key);
      else url.searchParams.set(key, value);
    }
    window.history[push ? 'pushState' : 'replaceState'](window.history.state, '', url);
  }

  async #serverPage(page) {
    if (this.#cache.has(page)) return this.#cache.get(page);
    const url = new URL(window.location.href);
    for (const key of ['show', 'columns', 'paging']) url.searchParams.delete(key);
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', this.closest('qdz-facets')?.getAttribute('section-id') ?? '');
    const response = await fetch(url);
    if (!response.ok) throw new Error(String(response.status));
    const doc = parseHTML(await response.text());
    adoptSectionStyles(doc);
    const items = [...doc.querySelectorAll('qdz-results [data-ref="grid"] > li')].map(fresh);
    this.#cache.set(page, items);
    return items;
  }

  async items(start, count) {
    const first = Math.floor(start / this.size) + 1;
    const last = Math.floor((start + count - 1) / this.size) + 1;
    const pages = [];
    for (let page = first; page <= last; page += 1) pages.push(this.#serverPage(page));
    const all = (await Promise.all(pages)).flat();
    const offset = start - (first - 1) * this.size;
    return all.slice(offset, offset + count);
  }

  #nodes(html) {
    const template = document.createElement('template');
    template.innerHTML = html.join('');
    return [...template.content.children];
  }

  get pages() {
    return Math.max(Math.ceil(this.total / this.show), 1);
  }

  async renderPage(page, { scroll = false } = {}) {
    const current = Math.min(Math.max(page, 1), this.pages);
    this.setAttribute('aria-busy', 'true');
    try {
      const start = (current - 1) * this.show;
      const html = await this.items(start, Math.min(this.show, this.total - start));
      this.grid.replaceChildren(...this.#nodes(html));
      this.#renderNumbers(current);
      if (scroll) this.closest('qdz-facets')?.scrollIntoView({ block: 'start', behavior: motion.scroll() });
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  #pageUrl(page) {
    const url = new URL(window.location.href);
    if (page > 1) url.searchParams.set('page', page);
    else url.searchParams.delete('page');
    return url.pathname + url.search;
  }

  #renderNumbers(current) {
    const nav = this.nav;
    if (!nav) return;
    const d = nav.dataset;
    const pages = this.pages;
    if (pages < 2) {
      nav.hidden = true;
      return;
    }
    nav.hidden = false;
    nav.className = 'qdz-pagination qdz-pagination--numbers';
    const set = new Set([1, pages, current - 1, current, current + 1].filter((n) => n >= 1 && n <= pages));
    const numbers = [...set].sort((a, b) => a - b);
    const parts = [];
    numbers.forEach((n, i) => {
      if (i > 0 && n - numbers[i - 1] > 1) parts.push('<li><span class="qdz-pagination__gap" aria-hidden="true">…</span></li>');
      parts.push(n === current
        ? `<li><span class="qdz-pagination__link qdz-pagination__link--current" aria-current="page" aria-label="${d.pageLabel} ${n}">${n}</span></li>`
        : `<li><a href="${this.#pageUrl(n)}" class="qdz-pagination__link" data-client-page="${n}" aria-label="${d.pageLabel} ${n}">${n}</a></li>`);
    });
    const step = (n, label, enabled, name) => (enabled ? `<a href="${this.#pageUrl(n)}" class="qdz-pagination__step" data-client-page="${n}" aria-label="${name}">${label}</a>` : `<span class="qdz-pagination__step" aria-disabled="true">${label}</span>`);
    nav.innerHTML = `<ul class="qdz-pagination__list" role="list"><li>${step(current - 1, d.previousShort, current > 1, d.previous)}</li>${parts.join('')}<li>${step(current + 1, d.nextShort, current < pages, d.next)}</li></ul><p class="qdz-pagination__note">${d.pageOf.replace('[page]', current).replace('[total]', pages)}</p>`;
  }

  async #start(page) {
    this.#observer?.disconnect();
    const count = Math.min(page * this.show, this.total);
    const html = await this.items(0, count);
    this.grid.replaceChildren(...this.#nodes(html));
    this.#loaded = html.length;
    this.#renderProgress();
  }

  async more() {
    if (this.#busy || this.#loaded >= this.total) return;
    this.#busy = true;
    this.#observer?.disconnect();
    this.setAttribute('aria-busy', 'true');
    try {
      const html = await this.items(this.#loaded, Math.min(this.show, this.total - this.#loaded));
      const nodes = this.#nodes(html);
      this.grid.append(...nodes);
      this.#loaded += html.length;
      const page = Math.ceil(this.#loaded / this.show);
      this.#replace({ page: page > 1 ? String(page) : null });
      this.#renderProgress();
      nodes[0]?.querySelector('a, button')?.focus({ preventScroll: true });
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
    }
  }

  #renderProgress() {
    const nav = this.nav;
    if (!nav) return;
    const d = nav.dataset;
    const mode = this.mode;
    const done = this.#loaded >= this.total;
    nav.hidden = this.total <= this.show && this.#loaded >= this.total;
    const template = mode === 'infinite' ? d.scrolling : d.showing;
    const text = template.replace('[shown]', this.#loaded).replace('[total]', this.total).replace('[noun]', d.noun);
    const percent = this.total ? Math.round((this.#loaded / this.total) * 100) : 100;
    nav.className = `qdz-pagination qdz-pagination--${mode}`;
    nav.innerHTML = `<p class="qdz-pagination__note" role="status">${mode === 'infinite' && !done ? '<span class="qdz-pagination__spinner" aria-hidden="true"></span>' : ''}${text}</p>${mode === 'load_more' ? `<span class="qdz-pagination__bar" aria-hidden="true"><span style="width: ${percent}%;"></span></span>` : ''}${done ? '' : `<a href="${this.#pageUrl(Math.ceil(this.#loaded / this.show) + 1)}" class="qdz-pagination__more" data-ref="more">${d.loadMore}</a>`}${mode === 'infinite' ? `<a href="${this.#pageUrl(1)}" class="qdz-pagination__list-link" data-ref="pages">${d.pageList}</a>` : ''}`;
    this.#observer?.disconnect();
    if (mode === 'infinite' && !done) {
      this.#observer ??= new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.more();
      }, { rootMargin: '0px 0px 300px 0px' });
      this.#observer.observe(nav);
    }
  }

  #onClick(event) {
    const page = event.target.closest('[data-client-page]');
    if (page) {
      event.preventDefault();
      event.stopPropagation();
      const n = Number(page.dataset.clientPage);
      this.#replace({ page: n > 1 ? String(n) : null }, true);
      this.renderPage(n, { scroll: true });
      return;
    }
    const pages = event.target.closest('[data-ref="pages"]');
    if (pages) {
      event.preventDefault();
      event.stopPropagation();
      this.#observer?.disconnect();
      this.#replace({ paging: 'numbers', page: null });
      this.renderPage(1, { scroll: true });
      return;
    }
    if (event.target.closest('[data-ref="more"]')) {
      event.preventDefault();
      event.stopPropagation();
      this.more();
      return;
    }
    if (this.mode === 'numbers' && this.show === this.size) return;
    const link = event.target.closest('.qdz-pagination a[data-page]');
    if (link) {
      event.preventDefault();
      event.stopPropagation();
      this.renderPage(Number(link.dataset.page), { scroll: true });
    }
  }
}

define('qdz-results', QdzResults);
